import { Handle3D } from "./Handle3D";
import { useMappedTexture, PLAIN_BOARD, PLAIN_STONE } from "./materials";

// All dimensions in metres
export const RUN_W = 3.6;
const KICK_H = 0.15, BASE_H = 0.72, BENCH_T = 0.04, DEPTH = 0.58, BENCH_D = 0.63, SPLASH_H = 0.6, OH_H = 0.72, OH_D = 0.35;
const GAP = 0.004, PANEL_T = 0.018;
const BENCH_Y = KICK_H + BASE_H; // top of carcass
const OH_Y = BENCH_Y + BENCH_T + SPLASH_H; // bottom of overheads
const BOARD_TILE = 1.2, STONE_TILE = 1.6;

// Cabinet modules across the run: widths and how each front is split
const MODULES = [
  { w: 0.6, fronts: "drawers3" }, { w: 0.6, fronts: "doorL" }, { w: 0.6, fronts: "doorR" },
  { w: 0.9, fronts: "drawers2" }, { w: 0.45, fronts: "doorL" }, { w: 0.45, fronts: "doorR" },
];
const splitFronts = (type, h) => {
  if (type === "drawers3") return [0.26, 0.26, 0.2].map((f) => f / 0.72 * h);
  if (type === "drawers2") return [0.5, 0.5].map((f) => f * h);
  return [h];
};

const Front = ({ x, y, w, h, z, tex, hw, drawer, side, upper }) => {
  const map = useMappedTexture(tex, x - w / 2, y - h / 2, w, h, BOARD_TILE);
  return (
    <group position={[x, y, z]}>
      <mesh castShadow receiveShadow>
        <boxGeometry args={[w - GAP, h - GAP, PANEL_T]} />
        <meshStandardMaterial map={map} color={map ? "#ffffff" : PLAIN_BOARD} roughness={0.55} metalness={0} />
      </mesh>
      {hw && <Handle3D kind={hw.kind} finish={hw.finish} w={w} h={h} z={PANEL_T / 2} drawer={drawer} side={side} upper={upper} />}
    </group>
  );
};

/** A row of cabinet fronts (base or overhead) laid across the run. */
const CabinetRow = ({ y0, h, depth, tex, hw, upper }) => {
  let x = -RUN_W / 2;
  const fronts = [];
  // overheads above wide modules become a pair of doors
  const mods = upper ? MODULES.flatMap((m) => (m.w > 0.6 ? [{ w: m.w / 2, fronts: "doorL" }, { w: m.w / 2, fronts: "doorR" }] : [m])) : MODULES;
  for (const m of mods) {
    const cx = x + m.w / 2;
    const isDrawer = m.fronts.startsWith("drawers");
    let y = y0 + h;
    for (const fh of splitFronts(upper ? "door" : m.fronts, h)) {
      y -= fh;
      fronts.push({ key: `${cx}-${y}`, x: cx, y: y + fh / 2, w: m.w, h: fh, drawer: isDrawer && !upper, side: m.fronts === "doorR" ? "R" : "L" });
    }
    x += m.w;
  }
  return (
    <group>
      <mesh position={[0, y0 + h / 2, -depth / 2]} receiveShadow castShadow>
        <boxGeometry args={[RUN_W, h, depth]} />
        <meshStandardMaterial color="#1a1a1f" roughness={0.9} />
      </mesh>
      {fronts.map(({ key, ...f }) => <Front key={key} {...f} z={PANEL_T / 2} tex={tex} hw={hw} upper={upper} />)}
    </group>
  );
};

const Slab = ({ pos, size, tex, tile }) => {
  const map = useMappedTexture(tex, pos[0] - size[0] / 2, pos[1] - size[1] / 2, size[0], Math.max(size[1], size[2]), tile);
  return (
    <mesh position={pos} castShadow receiveShadow>
      <boxGeometry args={size} />
      <meshStandardMaterial map={map} color={map ? "#ffffff" : PLAIN_STONE} roughness={0.22} metalness={0.02} envMapIntensity={1.1} />
    </mesh>
  );
};

export const KitchenRun = ({ boardTex, stoneTex, hw }) => (
  <group position={[0, 0, 0]}>
    {/* kickboard */}
    <mesh position={[0, KICK_H / 2, -0.06 - DEPTH / 2]} receiveShadow>
      <boxGeometry args={[RUN_W, KICK_H, DEPTH - 0.1]} />
      <meshStandardMaterial color="#121216" roughness={0.8} />
    </mesh>
    <CabinetRow y0={KICK_H} h={BASE_H} depth={DEPTH} tex={boardTex} hw={hw} />
    {/* benchtop with 20mm front overhang */}
    <Slab pos={[0, BENCH_Y + BENCH_T / 2, -DEPTH / 2 + (BENCH_D - DEPTH) / 2 + 0.01]} size={[RUN_W + 0.02, BENCH_T, BENCH_D]} tex={stoneTex} tile={STONE_TILE} />
    {/* splashback */}
    <Slab pos={[0, BENCH_Y + BENCH_T + SPLASH_H / 2, -DEPTH + 0.005]} size={[RUN_W, SPLASH_H, 0.01]} tex={stoneTex} tile={STONE_TILE} />
    {/* overheads, set back to the wall */}
    <group position={[0, 0, -(DEPTH - OH_D)]}>
      <CabinetRow y0={OH_Y} h={OH_H} depth={OH_D} tex={boardTex} hw={hw} upper />
    </group>
    {/* LED strip glow under the overheads */}
    {[-1.2, 0, 1.2].map((x) => <pointLight key={x} position={[x, OH_Y - 0.02, -DEPTH + 0.22]} intensity={0.12} distance={1.2} color="#ffe9c4" />)}
  </group>
);
